import { useRef } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
  shippingService,
  type CreateShippingPayload,
  type CreatePickupLocationPayload,
  type UpdatePickupLocationPayload,
  type EstimateFeePayload,
  type QueryShippingPayload,
  type AvailableOrder,
  type SimulatedBestFees,
} from '@/services/shipping.service';
import { orderService } from '@/services/order.service';
import {
  shippingKeys,
  type ShippingListQuery,
  type ShipperShippingListQuery,
  type GhtkLogsQuery,
} from '@/services/queryKeys';
import type { ReportShippingIncidentInput } from '@/services/shipping.service';
import { getApiEnvelopeMessage } from '@/utils/apiError';

// ─── List ──────────────────────────────────────────────────────────────────

export function useShippingList(query: ShippingListQuery) {
  return useQuery({
    queryKey: shippingKeys.list(query),
    queryFn: () => shippingService.list(query),
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

export const SHIPPER_ORDERS_PAGE_SIZE = 50;

export function useShipperShippingList(query: ShipperShippingListQuery) {
  return useInfiniteQuery({
    queryKey: shippingKeys.shipperList(query),
    queryFn: ({ pageParam }) => shippingService.list({
      ...query,
      page: pageParam,
      limit: SHIPPER_ORDERS_PAGE_SIZE,
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loadedThrough = lastPage.page * lastPage.limit;
      return loadedThrough < lastPage.total ? lastPage.page + 1 : undefined;
    },
    retry: 0,
    staleTime: 30_000,
  });
}

export function useClaimableShippingList(month: string) {
  return useInfiniteQuery({
    queryKey: shippingKeys.claimableForShipper(month),
    queryFn: ({ pageParam }) => shippingService.list({
      claimable_by_me: true,
      month,
      page: pageParam,
      limit: SHIPPER_ORDERS_PAGE_SIZE,
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loadedThrough = lastPage.page * lastPage.limit;
      return loadedThrough < lastPage.total ? lastPage.page + 1 : undefined;
    },
    retry: 0,
    staleTime: 10_000,
  });
}

export function useClaimShippingOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shippingOrderId: string) => shippingService.claim(shippingOrderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shippingKeys.lists() });
      message.success('Đã nhận vận đơn');
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không nhận được vận đơn'),
  });
}

export function useMyShippingStateCounts(month?: string, polling = false) {
  // Jitter cố định theo component lifecycle để các máy shipper không dồn request
  // vào cùng một nhịp 15 giây.
  const pollJitterMs = useRef(Math.floor(Math.random() * 3_001));
  return useQuery({
    queryKey: shippingKeys.myStateCounts(month),
    queryFn: () => shippingService.getMyStateCounts(month),
    staleTime: 15_000,
    refetchInterval: polling
      ? (query) => {
        if (query.state.fetchFailureCount >= 2) return 60_000;
        if (query.state.fetchFailureCount === 1) return 30_000;
        const serverInterval = query.state.data?.poll_after_ms ?? 15_000;
        const safeInterval = Math.min(60_000, Math.max(10_000, serverInterval));
        return safeInterval + pollJitterMs.current;
      }
      : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
}

export function useMyShippingProfileStats() {
  return useQuery({
    queryKey: shippingKeys.myProfileStats(),
    queryFn: () => shippingService.getMyProfileStats(),
    staleTime: 15_000,
    retry: 0,
  });
}

export function useMyShippingDeliveryStats(year: number) {
  return useQuery({
    queryKey: shippingKeys.myDeliveryStats(year),
    queryFn: () => shippingService.getMyDeliveryStats(year),
    staleTime: 60_000,
    retry: 0,
  });
}

// ─── Detail ────────────────────────────────────────────────────────────────

export function useShippingDetail(id: string) {
  return useQuery({
    queryKey: shippingKeys.detail(id),
    queryFn: () => shippingService.getById(id),
    enabled: !!id,
    retry: 0,
  });
}

// ─── Tracking ──────────────────────────────────────────────────────────────

export function useShippingTracking(id: string) {
  return useQuery({
    queryKey: shippingKeys.tracking(id),
    queryFn: () => shippingService.getTracking(id),
    enabled: !!id,
    retry: 0,
  });
}

// ─── Pickup Locations ──────────────────────────────────────────────────────

export function usePickupLocations() {
  return useQuery({
    queryKey: shippingKeys.pickupLocations(),
    queryFn: () => shippingService.listPickupLocations(),
    retry: 0,
  });
}

// ─── GHTK Logs ─────────────────────────────────────────────────────────────

export function useGhtkLogs(query: GhtkLogsQuery) {
  return useQuery({
    queryKey: shippingKeys.ghtkLogs(query),
    queryFn: () => shippingService.getGhtkLogs(query),
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useCreateShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateShippingPayload) => shippingService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
      qc.invalidateQueries({ queryKey: ['shipping', 'available-orders'] });
      message.success('Tạo đơn giao vận thành công');
    },
    onError: (error) => {
      qc.invalidateQueries({ queryKey: ['shipping', 'available-orders'] });
      message.error(getApiEnvelopeMessage(error) ?? 'Tạo đơn giao vận thất bại');
    },
  });
}

export function useShippingCarrierAvailability() {
  return useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: shippingService.listCarriers,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCallGHTK(shippingOrderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note?: string) => shippingService.callGHTK(shippingOrderId, note),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
      qc.invalidateQueries({ queryKey: shippingKeys.detail(shippingOrderId) });
      message.success(`Gọi GHTK thành công — Mã vận đơn: ${data.label}`);
    },
    onError: (error) => message.error(getApiEnvelopeMessage(error) ?? 'Gọi GHTK thất bại'),
  });
}

/**
 * Tải nhãn rồi mở tab in. Dùng mutation chứ không phải query: đây là hành động
 * người dùng chủ động, không nên tự chạy lại hay cache — URL nhãn của hãng có
 * thể hết hạn.
 */
export function useDownloadShippingLabel() {
  return useMutation({
    mutationFn: (id: string) => shippingService.downloadLabel(id),
    // Hiện đúng lý do BE trả về (chưa có mã vận đơn, hãng từ chối in…) thay vì
    // một câu chung chung cho mọi loại lỗi.
    onError: (error) =>
      message.error(
        getApiEnvelopeMessage(error) ?? 'Không tải được nhãn từ đơn vị vận chuyển',
      ),
  });
}

export function useUpdateTracking(shippingOrderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { tracking_number: string; carrier_name?: string }) =>
      shippingService.updateTracking(shippingOrderId, payload.tracking_number, payload.carrier_name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(shippingOrderId) });
      message.success('Cập nhật mã vận đơn thành công');
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Cập nhật mã vận đơn thất bại'),
  });
}

/**
 * Ghi nhận sự cố bàn giao (TCR-1521).
 *
 * Huỷ cache CẢ chi tiết lẫn danh sách: ghi sự cố xong người giao thường quay ngay
 * ra danh sách, mà huy hiệu đếm sự cố nằm trên thẻ ở danh sách đó.
 */
export function useReportShippingIncident(shippingOrderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReportShippingIncidentInput) =>
      shippingService.reportIncident(shippingOrderId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: shippingKeys.detail(shippingOrderId) });
      void queryClient.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
  });
}

export function useStartShippingReturn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shippingOrderId: string) => shippingService.startReturn(shippingOrderId),
    onSuccess: (_data, shippingOrderId) => {
      void queryClient.invalidateQueries({ queryKey: shippingKeys.detail(shippingOrderId) });
      void queryClient.invalidateQueries({ queryKey: shippingKeys.lists() });
      message.success('Đã chuyển đơn sang hoàn hàng');
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không chuyển được đơn sang hoàn hàng'),
  });
}

export function useCancelShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      shippingService.cancel(id, note),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
      qc.invalidateQueries({ queryKey: shippingKeys.detail(vars.id) });
      message.success('Đã hủy đơn giao vận');
    },
    /*
     * Hiện ĐÚNG câu BE trả về, đừng nuốt đi.
     *
     * Hủy đơn hỏng được vì nhiều lý do khác hẳn nhau, và BE đã nói rõ từng cái:
     * trạng thái không cho hủy, Best chỉ cho hủy trước khi lấy hàng, vận đơn
     * đang có thao tác Best chưa xong, trạng thái vừa đổi phải tải lại, hoặc
     * chính Best từ chối. Mỗi lý do là một việc khác nhau người dùng phải làm.
     *
     * Trước đây mọi lý do đều rơi về "Hủy đơn thất bại". TCR-1184 báo lên đúng
     * câu đó và KHÔNG ai lần ra được nguyên nhân — log không có, mà thứ duy nhất
     * người dùng thấy thì đã bị xóa sạch thông tin ngay tại đây.
     */
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Hủy đơn thất bại'),
  });
}

/**
 * Đối chiếu MỘT vận đơn với hãng — thay cho vòng cron `Order/Query` đã bỏ.
 *
 * KHÔNG tự `message.success` ở đây: "đã hỏi hãng xong" và "hãng vừa xác nhận"
 * là hai chuyện khác nhau, và chỉ nơi gọi mới biết nên hiện câu nào. Vẫn
 * invalidate ở cả hai nhánh vì kể cả khi chưa xác nhận, `data.best_reconciled_at`
 * đã đổi.
 */
/**
 * Nút "Làm mới" ở trang chi tiết: hỏi Best rồi đọc lại dữ liệu TCR-ERP.
 *
 * Thay cho cặp "Làm mới" (chỉ đọc lại) + "Đối chiếu hãng" (mới thật sự hỏi hãng,
 * lại chỉ hiện khi đơn "chưa xác định") — người dùng phải đoán nút nào mới lấy
 * được trạng thái mới.
 */
export function useRefreshShippingOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shippingService.refreshShippingOrder(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không hỏi được đơn vị vận chuyển'),
  });
}

export function useReconcileShippingOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shippingService.reconcileShippingOrder(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không đối chiếu được với đơn vị vận chuyển'),
  });
}

/**
 * Gửi lại vận đơn bị hãng từ chối.
 *
 * Thông báo dựng từ `submission_state` mà BE ĐỌC LẠI từ DB, không phải từ việc
 * lời gọi có ném lỗi hay không: gửi lại có thể ra "hãng đã nhận", "vẫn từ chối",
 * hoặc "chưa rõ kết quả" — báo "Đã gửi lại thành công" cho cả ba là nói dối
 * người dùng ở đúng chỗ họ cần biết sự thật nhất.
 */
export function useRetryShippingSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shippingService.retryShippingSubmission(id),
    onSuccess: (data, id) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
      if (data.submission_state === 'confirmed') {
        message.success(data.message);
      } else {
        message.warning(data.message);
      }
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không gửi lại được vận đơn'),
  });
}

export function useCompleteShipping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shippingService.complete(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      message.success('Hoàn thành đơn giao vận');
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không hoàn thành được đơn giao vận'),
  });
}

/**
 * Giả lập webhook Best.
 *
 * Hai phần chạy ở hai nhịp KHÁC NHAU, nên toast phải nói rõ cả hai. Phần giao hàng bên ta
 * (đóng đơn, `fulfillment_progress`) xong ngay khi API trả về; còn dấu vết phía hãng
 * (`data.best_status`, hành trình) đi qua hàng đợi rồi mới tới worker. Gộp làm một câu
 * "thành công" là người test tải lại thấy hành trình chưa đổi rồi tưởng hỏng.
 */
export function useSimulateBestWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      fees,
    }: { id: string; status?: string; fees?: SimulatedBestFees }) =>
      shippingService.simulateBestWebhook(id, status, fees),
    onSuccess: (data, { id }) => {
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      // Nói ĐÚNG việc vừa làm: chỉ mã 666 chốt thêm phần giao hàng bên ta, các mã
      // còn lại thuần webhook. Câu chung chung "đã chốt giao hàng" là sai với 22
      // mã còn lại kể từ khi bảng mã được mở hết.
      message.info(
        data.fulfillment_completed
          ? `Đã bắn mốc ${data.status} và chốt luôn phần giao hàng bên ta — vận đơn cập nhật sau khi worker xử lý`
          : `Đã bắn mốc ${data.status} vào hàng đợi webhook — vận đơn cập nhật sau khi worker xử lý`,
      );
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Không giả lập được webhook'),
  });
}

export function useCreatePickupLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePickupLocationPayload) =>
      shippingService.createPickupLocation(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.pickupLocations() });
      message.success('Thêm nơi lấy hàng thành công');
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Thêm nơi lấy hàng thất bại'),
  });
}

export function useUpdatePickupLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePickupLocationPayload }) =>
      shippingService.updatePickupLocation(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: shippingKeys.pickupLocations() });
      // Danh sách đơn chờ tạo vận đơn chặn theo địa chỉ nơi lấy hàng, sửa xong
      // phải tính lại ngay chứ không đợi cache hết hạn.
      qc.invalidateQueries({ queryKey: ['shipping', 'available-orders'] });
      message.success('Cập nhật nơi lấy hàng thành công');
    },
    onError: (error) =>
      message.error(getApiEnvelopeMessage(error) ?? 'Cập nhật nơi lấy hàng thất bại'),
  });
}

// ─── Confirmed Orders (for shipping creation step 1) ──────────────────────

export function useConfirmedOrders(q: { page?: number; page_size?: number; search?: string }) {
  return useQuery({
    queryKey: ['orders', 'confirmed-for-shipping', q],
    queryFn: () => orderService.listConfirmed(q),
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

// ─── Available Orders (for shipping creation step 1) ─────────────────────

export function useAvailableOrders(q: { page?: number; limit?: number; q?: string }) {
  return useQuery({
    queryKey: ['shipping', 'available-orders', q] as const,
    queryFn: (): Promise<{ data: AvailableOrder[]; total: number; page: number; limit: number }> =>
      shippingService.getAvailableOrders(q),
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

// ─── Estimate GHTK Fee (auto-query khi có payload) ────────────────────────

export function useEstimateGhtkFee(payload: EstimateFeePayload | null) {
  return useQuery({
    queryKey: ['shipping', 'estimate-fee', payload],
    queryFn: () => shippingService.estimateFee(payload!),
    enabled: !!payload,
    retry: 0,
    staleTime: 60 * 1000,
  });
}

export function useEstimateShippingFee(payload: EstimateFeePayload | null) {
  return useQuery({
    queryKey: ['shipping', 'estimate-fee', payload],
    queryFn: () => shippingService.estimateFee(payload!),
    enabled: !!payload,
    retry: 0,
    staleTime: 60 * 1000,
  });
}

export function useQueryBestOrders(payload: QueryShippingPayload | null) {
  return useQuery({
    queryKey: ['shipping', 'best-query', payload],
    queryFn: () => shippingService.queryBestOrders(payload!),
    enabled: !!payload,
    retry: 0,
  });
}

/** Gán người giao hàng cho vận đơn tự giao (TCR-1518). */
export function useSetShippingDeliveryman() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; staff_id?: string; name?: string; phone?: string }) =>
      shippingService.setDeliveryman(id, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
  });
}

export function useClearShippingDeliveryman() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shippingService.clearDeliveryman(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
  });
}

/** Ghi nhận đánh giá shipper cho vận đơn tự giao (TCR-1519). */
export function useSetShippingRating() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; stars: number; feedback?: string }) =>
      shippingService.setRating(id, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
  });
}

export function useClearShippingRating() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shippingService.clearRating(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: shippingKeys.detail(id) });
      qc.invalidateQueries({ queryKey: shippingKeys.lists() });
    },
  });
}
